// Shape-checks a portal-sent intake_form before it reaches the store.
const FIELD_TYPES = new Set(["text", "email", "phone"]);
/**
 * Fail-closed: a malformed intake_form is structural, not cosmetic. Stored
 * as-is, a shape like `{ enabled: true }` (no trigger) would surface later
 * when `noteAgentAnswerEdge()` reads `trigger.after_answers`. So any
 * validation failure among the structural fields (`enabled`, `trigger`,
 * `fields`) collapses the whole config to null ("not configured"). `title`
 * is the one cosmetic exception: a non-string title (including the explicit
 * `null` the server sends for "no title configured") just drops the key
 * rather than voiding the rest. Absent, null, and malformed all yield null.
 */
export function normalizeIntakeForm(raw) {
    if (typeof raw !== "object" || raw === null)
        return null;
    const f = raw;
    if (typeof f.enabled !== "boolean")
        return null;
    const trigger = typeof f.trigger === "object" && f.trigger !== null
        ? f.trigger
        : undefined;
    const afterAnswers = trigger?.after_answers;
    if (typeof afterAnswers !== "number" ||
        !Number.isInteger(afterAnswers) ||
        afterAnswers < 1 ||
        afterAnswers > 10) {
        return null;
    }
    if (!Array.isArray(f.fields) || f.fields.length === 0 || f.fields.length > 8)
        return null;
    const fields = [];
    for (const rawField of f.fields) {
        if (typeof rawField !== "object" || rawField === null)
            return null;
        const field = rawField;
        if (typeof field.key !== "string" || field.key.length === 0)
            return null;
        if (typeof field.label !== "string")
            return null;
        if (!FIELD_TYPES.has(field.type))
            return null;
        if (typeof field.required !== "boolean")
            return null;
        fields.push({
            key: field.key,
            label: field.label,
            type: field.type,
            required: field.required,
        });
    }
    return {
        enabled: f.enabled,
        trigger: { after_answers: afterAnswers },
        fields,
        // Present only when a string was sent, so an absent title stays a
        // genuinely absent key rather than a key set to `undefined`.
        ...(typeof f.title === "string" ? { title: f.title } : {}),
    };
}
